import type { ReactNode } from "react";
import {
  EXTERNAL_LINK_PROPS,
  MARKETING_TERMS_URL,
  PRIVACY_POLICY_URL,
} from "@/lib/legal";

// The Broker Portal's Terms of Service (BRK-S149) — a PUBLIC route, mounted in
// the marketing shell beside /login so a producer can read it before signing in
// and a reviewer can read it without an account.
//
// Three things shape the substance and must survive every future edit:
//
//   1. **These terms are subordinate to the producer/agency agreement.** That
//      written agreement is the real contract with an agency; this document
//      governs access to and use of the portal software only. Section 3 is the
//      clause that keeps a page nobody negotiated from amending one they did.
//   2. **The privacy policy is linked, never copied.** One entity, one policy
//      (see src/lib/legal.ts).
//   3. **This page is public-reachable**, so the marketing site's
//      confidentiality floor applies: no carrier names, no rates, retentions,
//      SIR/deductible schedules, credit thresholds, classification tables,
//      loss-ratio figures or capacity structure. Pinned by
//      test/app/TermsPage.test.tsx.
//
// The entity definition, governing law (New Jersey), venue (Monmouth County)
// and contacts are deliberately IDENTICAL to the marketing site's terms — one
// company should not describe itself two ways. There is no arbitration clause
// and no class-action waiver, matching WWW-S053.
//
// Content is a single ordered list of sections so the table of contents and the
// body cannot drift apart: numbering, anchors and headings all derive from it.

/** The effective date, machine-readable. Bump it whenever the terms change. */
export const TERMS_EFFECTIVE_DATE = "2026-09-09";
const TERMS_EFFECTIVE_DISPLAY = "September 9, 2026";

/** Body paragraph — one measure, one colour, defined once. */
function P({ children }: { children: ReactNode }) {
  return <p className="mt-4 text-white/70">{children}</p>;
}

/** Bulleted list in the same register as the prose. */
function Bullets({ items }: { items: ReactNode[] }) {
  return (
    <ul className="mt-4 list-disc space-y-2 pl-5 text-white/70 marker:text-white/30">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

/** Inline link. White + underlined rather than accent-coloured: Electric Violet
 *  on the ink surface does not clear AA for body-sized text, and the brand book
 *  reserves it for large/decorative type. */
function A({
  href,
  external = false,
  children,
}: {
  href: string;
  external?: boolean;
  children: ReactNode;
}) {
  return (
    <a
      href={href}
      {...(external ? EXTERNAL_LINK_PROPS : {})}
      className="text-white underline decoration-white/40 underline-offset-2 transition-colors hover:decoration-white"
    >
      {children}
    </a>
  );
}

const PRIVACY_LINK = (
  <A href={PRIVACY_POLICY_URL} external>
    Privacy Policy
  </A>
);

interface Section {
  id: string;
  title: string;
  body: ReactNode;
}

const SECTIONS: Section[] = [
  {
    id: "acceptance",
    title: "Acceptance of these terms",
    body: (
      <>
        <P>
          These Terms of Service govern your access to and use of the Signal
          Specialty Broker Portal at broker.signalspecialty.com (the{" "}
          <strong>&ldquo;Portal&rdquo;</strong>). By signing in to the Portal or
          using it, you agree to them. If you do not agree, do not use the
          Portal.
        </P>
        <P>
          You are almost certainly using the Portal on behalf of an agency or
          brokerage. If so, you are agreeing to these terms on that
          organization&rsquo;s behalf as well as your own, and you confirm that
          you are authorized to do so. In these terms{" "}
          <strong>&ldquo;your agency&rdquo;</strong> means that organization and{" "}
          <strong>&ldquo;you&rdquo;</strong> means both you personally and your
          agency.
        </P>
      </>
    ),
  },
  {
    id: "who-we-are",
    title: "Who we are",
    body: (
      <P>
        In these terms, <strong>&ldquo;Signal Specialty,&rdquo;</strong>{" "}
        <strong>&ldquo;we,&rdquo;</strong> <strong>&ldquo;us&rdquo;</strong> and{" "}
        <strong>&ldquo;our&rdquo;</strong> mean Signal Specialty, Inc. and its
        affiliates, including Signal Specialty Insurance Services, LLC, the
        operating entity through which our insurance activities are conducted.
        Our mailing address is 25 Bridge Street, Red Bank, New Jersey 07701.
      </P>
    ),
  },
  {
    id: "agency-agreement",
    title: "Your producer/agency agreement comes first",
    body: (
      <>
        <P>
          <strong>
            These terms govern access to and use of the Portal software only.
          </strong>{" "}
          They are not your appointment, and they are not the commercial
          agreement between us. Any written producer, agency, brokerage or
          distribution agreement between Signal Specialty and your agency (the{" "}
          <strong>&ldquo;Agency Agreement&rdquo;</strong>) governs the business
          relationship &mdash; authority, compensation, licensing, service
          levels, data handling commitments, confidentiality, and everything
          else it addresses.
        </P>
        <P>
          <strong>
            Where these terms and the Agency Agreement conflict, the Agency
            Agreement controls, and nothing in these terms amends, replaces,
            waives or limits it.
          </strong>{" "}
          Where the Agency Agreement is silent on a subject these terms address,
          these terms apply to your use of the Portal.
        </P>
        <P>
          Using the Portal does not by itself appoint your agency, grant any
          binding, underwriting or claims authority, or oblige us to accept any
          submission or offer terms of any kind.
        </P>
      </>
    ),
  },
  {
    id: "what-it-is",
    title: "What the Portal is, and is not",
    body: (
      <>
        <P>
          The Portal is a tool. It lets an authorized producer create and manage
          insured account records, prepare and submit business, upload
          supporting documents, request indications and quotes, track a
          submission&rsquo;s progress, and read back what was submitted.
        </P>
        <P>The Portal itself is not:</P>
        <Bullets
          items={[
            "an offer to sell, solicit, bind, place or renew any policy of insurance or any surety obligation;",
            "a policy, a binder, a certificate, or evidence of coverage;",
            "insurance, legal, tax, financial or other professional advice; or",
            "a grant of binding authority, underwriting authority or claims authority to anyone.",
          ]}
        />
        <P>
          The Portal is a client application. The records it displays are held
          and maintained by our policy administration system, which is the
          system of record; what you see in the Portal is a view of that system
          at a point in time.
        </P>
      </>
    ),
  },
  {
    id: "eligibility",
    title: "Who may use the Portal",
    body: (
      <P>
        The Portal is for authorized personnel of an agency that is appointed by
        us or is in the process of being onboarded, using it for business
        purposes. You must be at least 18 years old, and you must hold whatever
        licences and authorizations your role and your jurisdiction require. The
        Portal is not for consumers shopping for insurance, and it is not
        intended for children.
      </P>
    ),
  },
  {
    id: "roles",
    title: "Roles, and who your agency invites",
    body: (
      <>
        <P>
          Portal users hold one of two roles. <strong>Owners</strong> can
          maintain the agency&rsquo;s own record in addition to their own work;{" "}
          <strong>Staff</strong> see the agency record read-only. Both roles can
          see the agency&rsquo;s accounts, submissions and the information those
          carry.
        </P>
        <P>
          <strong>
            Your agency is responsible for who it invites and what they do.
          </strong>{" "}
          An Owner who invites a user is confirming that the person is
          authorized to act for the agency and, where applicable, properly
          licensed. Your agency is responsible for keeping that list current
          &mdash; including telling us promptly when someone leaves or changes
          role, so their access can be removed &mdash; and for every action
          taken under its users&rsquo; credentials. We may act on any
          instruction we reasonably believe came from an authorized user of your
          agency.
        </P>
      </>
    ),
  },
  {
    id: "security",
    title: "Your credentials and account security",
    body: (
      <>
        <P>
          Portal accounts are individual.{" "}
          <strong>
            Do not share your credentials, and do not use a shared or generic
            login.
          </strong>{" "}
          Each person who needs access gets their own account. Choose a strong,
          unique password, and turn on a second factor or a passkey where the
          Portal offers one.
        </P>
        <P>
          If the Portal offers you a <strong>passkey</strong>, a passkey is a
          public-key credential stored by your device or your password manager:
          your device keeps the private key and proves possession of it to us,
          and we hold only the corresponding public key. If your device unlocks
          the passkey with a fingerprint, a face scan or a device PIN, that
          check happens on the device &mdash;{" "}
          <strong>
            your biometric data never leaves your device, and we never receive
            it, store it or have access to it.
          </strong>
        </P>
        <P>
          Tell us promptly, using the contacts in section 25, if you believe a
          credential has been lost or compromised, if an account should have
          been closed and has not been, or if you see activity in the Portal
          that your agency did not carry out. You are responsible for activity
          under your credentials until you do.
        </P>
      </>
    ),
  },
  {
    id: "accuracy",
    title: "Information you submit",
    body: (
      <>
        <P>
          You agree that everything you submit through the Portal &mdash;
          account and insured details, exposure and operational information,
          personnel and ownership information, uploaded documents, and every
          answer to an eligibility or underwriting question &mdash; is accurate,
          current and complete to the best of your knowledge, and that you are
          authorized to submit it.
        </P>
        <P>
          This matters more here than in an ordinary web application. We, and
          the insurers we place business with, rely on what you submit to
          evaluate, price and issue coverage. Inaccurate, incomplete or
          out-of-date information can lead to a withdrawn indication, a
          rescinded or voided policy, or a declined claim. If you learn that
          something you submitted was wrong or has changed, correct it in the
          Portal or tell us.
        </P>
      </>
    ),
  },
  {
    id: "third-party-information",
    title: "Information about other people",
    body: (
      <>
        <P>
          Much of what you enter is information about someone other than you
          &mdash; an insured business, its owners, officers and personnel, and,
          for surety, indemnitors and their spouses. Some of it is sensitive:
          government identification numbers, dates of birth, driver&rsquo;s
          licence details and financial information.
        </P>
        <P>
          <strong>
            You represent and warrant that, for every individual and business
            whose information you submit, you have the authority and any notice,
            consent or authorization required by law to collect that information
            and to provide it to us, and to permit us and the insurers we work
            with to use it to evaluate, price, place, issue and service
            insurance and surety business.
          </strong>{" "}
          You also agree that you have complied with the privacy, data
          protection and insurance laws that apply to your agency in obtaining
          it.
        </P>
        <P>
          Submit only what the Portal asks for, and only what is needed for the
          business you are placing. How we handle personal information once we
          receive it is described in our {PRIVACY_LINK}.
        </P>
      </>
    ),
  },
  {
    id: "confidentiality",
    title: "Confidentiality and permitted use of what you see",
    body: (
      <>
        <P>
          The Portal shows you information that is not yours to redistribute:
          indications, quotes and their terms, underwriting outcomes and the
          reasons behind them, product configuration and eligibility logic,
          pricing detail, and non-public information about the insurers we place
          business with. Treat all of it as our confidential information.
        </P>
        <P>
          You may use it only to place and service business with us, and you
          agree not to:
        </P>
        <Bullets
          items={[
            "disclose it to anyone outside your agency, other than the insured or prospective insured it concerns, and then only as needed to place or service that business;",
            "use it to negotiate with, or to benefit, a competing market;",
            "compile, aggregate, benchmark or publish it; or",
            "keep or use it after your agency stops doing business with us, except where you must retain it to comply with a law or a regulator.",
          ]}
        />
        <P>
          Information about an insured is theirs, and your obligations to them
          are your own. Nothing in this section reduces the confidentiality
          provisions of the Agency Agreement, which control where they conflict
          with this one.
        </P>
      </>
    ),
  },
  {
    id: "acceptable-use",
    title: "Acceptable use",
    body: (
      <>
        <P>You agree not to:</P>
        <Bullets
          items={[
            "use the Portal for any unlawful purpose, or in a way that breaches any insurance, privacy, anti-spam or export law;",
            "submit information that is false, misleading, fraudulent, or submitted in someone else’s name without their authority;",
            "access the Portal with credentials that are not your own, or let anyone else use yours;",
            "use bots, scrapers, crawlers, headless browsers or any other automated means to access the Portal, extract data from it, or submit anything through it;",
            "probe, scan or test the vulnerability of the Portal or any related system, or breach or circumvent any authentication, rate limit or other security or access control, except through the coordinated disclosure route in section 25;",
            "reverse engineer, decompile or disassemble any part of the Portal, or attempt to derive its source code, underlying logic or models, except to the extent that restriction is unenforceable by law;",
            "copy, frame, mirror or republish any substantial part of the Portal, or use it — or anything you learn from it — to build or improve a competing product or service;",
            "interfere with or disrupt the Portal, or place an unreasonable load on it; or",
            "encourage or help anyone else do any of these things.",
          ]}
        />
      </>
    ),
  },
  {
    id: "not-coverage",
    title: "Quotes, binders and policies shown in the Portal",
    body: (
      <>
        <P>
          <strong>
            What the Portal displays is not coverage, and no figure shown in it
            is a promise to insure.
          </strong>{" "}
          Coverage is only ever provided by an issued policy, on the terms of
          that policy, and by the insurer that issues it. Where the Portal and
          an issued policy disagree, the policy governs.
        </P>
        <Bullets
          items={[
            <>
              An <strong>indication</strong> is an estimate for discussion. It
              is not a quote, it is not an offer, and it is subject to
              underwriting review, to complete and accurate information, and to
              change or withdrawal.
            </>,
            <>
              A <strong>quote</strong> is an offer only on its own terms,
              including its stated expiry, subjectivities and conditions. It
              lapses when it expires and may be withdrawn or amended before
              then, including if information changes or proves inaccurate.
            </>,
            <>
              A <strong>binder</strong> provides only the coverage it states,
              for only as long as it states, subject to its conditions and to
              the issuing insurer&rsquo;s authority.
            </>,
            <>
              Premiums, fees, taxes and other amounts shown are estimates until
              the policy is issued, and remain subject to audit, endorsement,
              cancellation and regulatory adjustment.
            </>,
            <>
              A status, timestamp or document in the Portal is a record of our
              processing &mdash; it is not notice to any insurer, and it does
              not itself bind, renew, endorse or cancel anything.
            </>,
          ]}
        />
        <P>
          Do not represent to an insured, a prospective insured or anyone else
          that coverage is in force on the basis of anything shown in the
          Portal.
        </P>
      </>
    ),
  },
  {
    id: "availability",
    title: "Availability and changes to the Portal",
    body: (
      <>
        <P>
          We may change, add to, suspend or discontinue the Portal, or any part
          of it, at any time, including for maintenance. We will try to give
          reasonable notice of a planned interruption or a materially disruptive
          change, but we may act without notice where security, legal compliance
          or the integrity of the service requires it.
        </P>
        <P>
          <strong>
            These terms make no uptime or availability commitment.
          </strong>{" "}
          If any service level applies to your agency, it is in the Agency
          Agreement, and that agreement is where to look for it. Submitting
          business does not depend on the Portal &mdash; if it is unavailable
          and something is time-sensitive, contact us using the details in
          section 25.
        </P>
      </>
    ),
  },
  {
    id: "suspension",
    title: "Suspension and termination of access",
    body: (
      <>
        <P>
          We may suspend or terminate any user&rsquo;s access, or your
          agency&rsquo;s access, at any time &mdash; immediately and without
          notice where we reasonably suspect a security problem, a breach of
          these terms, fraud, or a legal or regulatory requirement, and
          otherwise on reasonable notice. Your agency may ask us to remove a
          user at any time.
        </P>
        <P>
          Losing Portal access does not by itself end the Agency Agreement, and
          ending the Agency Agreement ends Portal access. Records we hold remain
          subject to our retention obligations, described in our {PRIVACY_LINK}.
          Sections that by their nature should survive do; see section 24.
        </P>
      </>
    ),
  },
  {
    id: "ip",
    title: "Intellectual property",
    body: (
      <>
        <P>
          The Portal and everything in it &mdash; software, text, layout,
          graphics, logos, icons, the underlying models and logic, and the
          arrangement of it all &mdash; is owned by Signal Specialty or its
          licensors and is protected by copyright, trademark and other laws.
          Subject to these terms, we grant your agency a limited, revocable,
          non-exclusive, non-transferable, non-sublicensable right to access and
          use the Portal for the internal business purpose of placing and
          servicing business with us. We reserve every right not expressly
          granted.
        </P>
        <P>
          <strong>Signal</strong>, <strong>Signal Specialty</strong>, and the
          Signal marks and logos are our trademarks. You may not use them
          without our prior written permission, except as the Agency Agreement
          allows.
        </P>
        <P>
          The typefaces the Portal uses are licensed to us, not ours to give
          away. The font files it serves may not be extracted, copied,
          redistributed or used anywhere else.
        </P>
        <P>
          Information your agency submits stays yours (or the insured&rsquo;s).
          You grant us a non-exclusive right to use it to operate the Portal,
          evaluate and place business, meet our legal and regulatory
          obligations, and otherwise as described in our {PRIVACY_LINK} and the
          Agency Agreement.
        </P>
      </>
    ),
  },
  {
    id: "feedback",
    title: "Feedback",
    body: (
      <P>
        If you send us suggestions, bug reports, ideas or feedback about the
        Portal, we may use them freely, for any purpose, without obligation or
        compensation to you. Please do not send us anything you consider
        confidential or proprietary as feedback.
      </P>
    ),
  },
  {
    id: "third-party-services",
    title: "Third-party services",
    body: (
      <P>
        The Portal uses services provided by other companies &mdash; including a
        live chat service, provided by Crisp, that appears as a chat window
        inside the Portal, and an address lookup service. We do not control
        those services, and your use of one is also governed by that
        company&rsquo;s own terms. Chat is for support; it is not a channel for
        binding instructions, for notice of a claim, or for sensitive personal
        information, and nothing said in chat varies these terms or the Agency
        Agreement. What those services store and why is described in our{" "}
        {PRIVACY_LINK}.
      </P>
    ),
  },
  {
    id: "privacy",
    title: "Privacy",
    body: (
      <>
        <P>
          Signal Specialty maintains a single, entity-wide {PRIVACY_LINK} that
          covers this Portal along with our other systems. It explains what
          personal information we collect, how we use and share it, how long we
          keep it, and the choices available. It forms part of these terms.
        </P>
        <P>
          We publish it in one place on purpose, so there is one description of
          our practices rather than several that drift apart. Read it at{" "}
          <A href={PRIVACY_POLICY_URL} external>
            www.signalspecialty.com/privacy/
          </A>
          .
        </P>
      </>
    ),
  },
  {
    id: "warranties",
    title: "Disclaimer of warranties",
    body: (
      <>
        <P>
          The Portal is provided <strong>&ldquo;as is&rdquo;</strong> and{" "}
          <strong>&ldquo;as available.&rdquo;</strong> To the fullest extent
          permitted by law, we disclaim all warranties, express or implied,
          including any implied warranties of merchantability, fitness for a
          particular purpose, title and non-infringement.
        </P>
        <P>
          We do not warrant that the Portal will be uninterrupted, timely,
          secure or error-free, that defects will be corrected, or that it is
          free of harmful components. We take care that what it displays is
          accurate, but we do not warrant that any figure, status or document
          shown in it is complete, accurate or current.
        </P>
        <P>
          <strong>
            This section limits our obligations under these terms only. It does
            not limit the obligations of any insurer under a policy it issues,
            and it does not limit our obligations under the Agency Agreement.
          </strong>
        </P>
        <P>
          Some jurisdictions do not allow the exclusion of certain warranties,
          so parts of this section may not apply to you.
        </P>
      </>
    ),
  },
  {
    id: "liability",
    title: "Limitation of liability",
    body: (
      <>
        <P>
          To the fullest extent permitted by law, neither Signal Specialty nor
          its officers, directors, employees, agents or service providers will
          be liable for any indirect, incidental, special, consequential,
          exemplary or punitive damages, or for any loss of profits, revenue,
          data, goodwill or business opportunity, arising out of or relating to
          your use of the Portal &mdash; whether the claim is brought in
          contract, tort, strict liability or otherwise, and even if we have
          been advised that such damages are possible.
        </P>
        <P>
          To the fullest extent permitted by law, our total liability for all
          claims arising out of or relating to the Portal and these terms will
          not exceed one thousand US dollars (US$1,000).
        </P>
        <P>
          <strong>
            These limits apply to the Portal and these terms only. They do not
            limit the obligations of any insurer under a policy it issues, they
            do not limit our obligations under the Agency Agreement, and they do
            not affect any claim arising under that agreement.
          </strong>
        </P>
        <P>
          Nothing in these terms excludes or limits liability that cannot
          lawfully be excluded or limited, including liability for fraud. Some
          jurisdictions do not allow certain limitations, so parts of this
          section may not apply to you.
        </P>
      </>
    ),
  },
  {
    id: "indemnity",
    title: "Indemnification",
    body: (
      <P>
        You agree to indemnify and hold harmless Signal Specialty and its
        officers, directors, employees and agents from any claim, loss,
        liability, cost or expense (including reasonable legal fees) arising out
        of your use of the Portal, your breach of these terms, your submission
        of inaccurate information or of information you were not authorized to
        provide, your violation of any law, or your infringement of anyone
        else&rsquo;s rights. We may take over the defence of any such claim at
        our own expense, and you agree to cooperate with us if we do. Where the
        Agency Agreement also addresses indemnity, that agreement controls.
      </P>
    ),
  },
  {
    id: "changes",
    title: "Changes to these terms",
    body: (
      <P>
        We may update these terms. When we do, we will change the effective date
        at the top of this page. Changes apply from the date they are posted,
        and continued use of the Portal after that means you accept them. If a
        change materially affects your rights, we will take reasonable steps to
        make it noticeable &mdash; in the Portal, or by contacting your agency
        &mdash; rather than relying on you to re-read the page.
      </P>
    ),
  },
  {
    id: "governing-law",
    title: "Governing law and venue",
    body: (
      <>
        <P>
          These terms and any dispute arising out of them or out of your use of
          the Portal are governed by the laws of the State of New Jersey,
          without regard to its conflict-of-laws rules.
        </P>
        <P>
          You and we agree that any such dispute will be brought exclusively in
          the state or federal courts located in Monmouth County, New Jersey,
          and you consent to the personal jurisdiction of those courts and waive
          any objection to venue there. Where the Agency Agreement specifies a
          forum for disputes under that agreement, it controls for those
          disputes.
        </P>
      </>
    ),
  },
  {
    id: "general",
    title: "General",
    body: (
      <Bullets
        items={[
          <>
            <strong>Severability.</strong> If any provision of these terms is
            held unenforceable, it will be limited or removed to the minimum
            extent necessary and the rest will remain in effect.
          </>,
          <>
            <strong>No waiver.</strong> If we do not enforce a provision, that
            is not a waiver of our right to enforce it later.
          </>,
          <>
            <strong>Assignment.</strong> You may not assign or transfer these
            terms without our written consent. We may assign them to an
            affiliate or in connection with a financing, merger, acquisition or
            sale of our business.
          </>,
          <>
            <strong>No third-party beneficiaries.</strong> These terms are
            between you and us, and give no rights to anyone else.
          </>,
          <>
            <strong>Entire agreement.</strong> These terms and our{" "}
            {PRIVACY_LINK} are the entire agreement between you and us about the
            Portal, and supersede anything said or written about the Portal
            before. They do not supersede the Agency Agreement, which controls
            to the extent it conflicts with these terms.
          </>,
          <>
            <strong>Survival.</strong> Sections 3, 8 through 11, and 15 through
            24 survive any end of your access to the Portal.
          </>,
          <>
            <strong>Notices.</strong> We may give you notices in the Portal, by
            email to the address on your account, or through your agency&rsquo;s
            contacts on file. Notices to us go to the addresses in section 25.
          </>,
        ]}
      />
    ),
  },
  {
    id: "contact",
    title: "How to contact us",
    body: (
      <dl className="mt-4 space-y-3 text-white/70">
        {[
          {
            term: "Broker support",
            detail: (
              <A href="mailto:support@signalspecialty.com">
                support@signalspecialty.com
              </A>
            ),
          },
          {
            term: "Privacy",
            detail: (
              <A href="mailto:privacy@signalspecialty.com">
                privacy@signalspecialty.com
              </A>
            ),
          },
          {
            term: "Security vulnerability reports",
            detail: (
              <A href="mailto:security@signalspecialty.com">
                security@signalspecialty.com
              </A>
            ),
          },
          {
            term: "Telephone",
            detail: <A href="tel:+18887441605">(888)&nbsp;744-1605</A>,
          },
          {
            term: "Mail",
            detail: (
              <>
                Signal Specialty
                <br />
                25 Bridge Street
                <br />
                Red Bank, New Jersey 07701
              </>
            ),
          },
        ].map(({ term, detail }) => (
          <div key={term}>
            <dt className="font-medium text-white">{term}</dt>
            <dd className="mt-1">{detail}</dd>
          </div>
        ))}
      </dl>
    ),
  },
];

export function TermsPage() {
  return (
    <article className="mx-auto max-w-3xl">
      <header>
        <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">
          Broker Portal Terms of Service
        </h1>
        <p className="mt-4 text-sm font-medium uppercase tracking-wide text-white/50">
          Effective{" "}
          <time dateTime={TERMS_EFFECTIVE_DATE} className="text-white/80">
            {TERMS_EFFECTIVE_DISPLAY}
          </time>
        </p>
        <p className="mt-6 text-lg text-white/70">
          These terms govern access to and use of the Signal Specialty Broker
          Portal. The short version: the Portal is a tool for placing and
          tracking business, your agency&rsquo;s written producer/agency
          agreement is the real contract and comes first, and nothing you see in
          the Portal is coverage &mdash; only an issued policy is.
        </p>
        <p className="mt-4 text-sm text-white/50">
          Looking for the terms of our public website instead? They are at{" "}
          <A href={MARKETING_TERMS_URL} external>
            www.signalspecialty.com/terms/
          </A>
          .
        </p>
      </header>

      <nav
        aria-label="On this page"
        className="mt-10 rounded-lg border border-white/10 bg-white/5 p-5"
      >
        <h2 className="text-base font-semibold text-white">On this page</h2>
        <ol className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1.5 text-sm sm:grid-cols-2">
          {SECTIONS.map((section, i) => (
            <li key={section.id}>
              <a
                href={`#${section.id}`}
                className="text-white/70 underline decoration-white/20 underline-offset-2 transition-colors hover:text-white hover:decoration-white/60"
              >
                {i + 1}. {section.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      <div className="mt-12 space-y-10">
        {SECTIONS.map((section, i) => (
          <section key={section.id} id={section.id} className="scroll-mt-24">
            <h2 className="text-xl font-semibold text-white sm:text-2xl">
              {i + 1}. {section.title}
            </h2>
            {section.body}
          </section>
        ))}
      </div>

      <p className="mt-12 border-t border-white/10 pt-6 text-sm text-white/50">
        Questions about these terms, or about how they sit alongside your agency
        agreement? Write to{" "}
        <A href="mailto:support@signalspecialty.com">
          support@signalspecialty.com
        </A>
        .
      </p>
    </article>
  );
}
